/**
 * Physical audit: walk a location, scan what is there, see what does not match.
 *
 * The system's claim about where things are has never been checked against the
 * racks. That check is what makes an asset register trustworthy rather than
 * merely tidy, and it is the thing an auditor asks for.
 *
 * Three outcomes matter, and the third is the interesting one:
 *   - expected and found      - the boring majority
 *   - expected and not found  - missing, chase it
 *   - found but not expected  - the system was wrong about where this was
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardCheck, Play, ScanLine, Square } from 'lucide-react';
import {
  ApiClientError,
  type StockTakeDetail,
  type StockTakeRow,
} from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input, Select } from '../../components/ui/field.js';
import { Badge, Card, CardBody, Code, EmptyState } from '../../components/ui/primitives.js';
import {
  closeStockTake,
  getStockTake,
  isNetworkError,
  listStockTakes,
  scanStockTake,
  startStockTake,
} from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { useMasterOptions } from '../../db/queries.js';
import { cn } from '../../lib/cn.js';

export function StockTakePage() {
  const canRun = useCan('create', 'scans');
  const locations = useMasterOptions('locations');

  const [takes, setTakes] = useState<StockTakeRow[] | undefined>(undefined);
  const [active, setActive] = useState<StockTakeDetail | null>(null);
  const [locationId, setLocationId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await listStockTakes();
      setTakes(rows);
      const open = rows.find((r) => r.status === 'OPEN');
      setActive(open ? await getStockTake(open.id) : null);
    } catch (e) {
      setTakes([]);
      setError(
        isNetworkError(e) ? 'No connection. A count runs against the server.' : 'Could not load.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep focus in the box so a handheld scanner types straight into it, the
  // same trick the Android app uses.
  useEffect(() => {
    if (active) inputRef.current?.focus({ preventScroll: true });
  }, [active, flash]);

  const summary = useMemo(() => {
    const lines = active?.lines ?? [];
    return {
      expected: lines.filter((l) => l.expected).length,
      found: lines.filter((l) => l.found).length,
      missing: lines.filter((l) => l.expected && !l.found).length,
      unexpected: lines.filter((l) => !l.expected && l.found).length,
    };
  }, [active]);

  async function begin() {
    if (!locationId) {
      setError('Choose the location you are counting.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await startStockTake(locationId);
      await load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Could not start the count.');
    } finally {
      setBusy(false);
    }
  }

  async function submitScan(e: React.FormEvent) {
    e.preventDefault();
    const value = code.trim();
    if (!value || !active) return;
    setError(null);
    try {
      const line = await scanStockTake(active.id, value);
      setFlash(
        line.unexpected
          ? `${line.mould?.assetCode ?? value} - not expected here`
          : `${line.mould?.assetCode ?? value} counted`,
      );
      setCode('');
      setActive(await getStockTake(active.id));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'That code could not be counted.');
      setCode('');
    }
  }

  async function finish() {
    if (!active) return;
    setBusy(true);
    try {
      await closeStockTake(active.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Could not close the count.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page
      title="Stock take"
      description="Count what is physically on the racks, and compare it with what the system believes."
      actions={
        active && canRun ? (
          <Button variant="secondary" onClick={() => void finish()} loading={busy}>
            <Square className="size-4" /> Close count
          </Button>
        ) : null
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {!active ? (
        <>
          {canRun ? (
            <Card className="mb-4">
              <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <Field label="Location to count" className="flex-1">
                  <Select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    placeholder="Choose a location"
                    options={locations ?? []}
                  />
                </Field>
                <Button variant="primary" onClick={() => void begin()} loading={busy}>
                  <Play className="size-4" /> Start count
                </Button>
              </CardBody>
            </Card>
          ) : null}

          {takes?.length ? (
            <div className="overflow-x-auto rounded-xl border border-border bg-surface">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-sunken/60 text-left">
                    <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Reference</th>
                    <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Location</th>
                    <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Started</th>
                    <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">By</th>
                    <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {takes.map((t) => (
                    <tr key={t.id}>
                      <td className="px-3 py-2"><Code>{t.reference}</Code></td>
                      <td className="px-3 py-2">{t.location?.name ?? '-'}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(t.startedAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{t.startedBy?.name ?? '-'}</td>
                      <td className="px-3 py-2">
                        <Badge tone={t.status === 'OPEN' ? 'warning' : 'success'}>
                          {t.status === 'OPEN' ? 'Open' : 'Closed'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={<ClipboardCheck />}
              title="No counts yet"
              description="Start one to check a location's racks against what the system believes is there."
            />
          )}
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-2 sm:grid-cols-4">
            <Tile label="Expected here" value={summary.expected} />
            <Tile label="Counted" value={summary.found} tone="ok" />
            <Tile label="Missing" value={summary.missing} tone={summary.missing ? 'bad' : undefined} />
            <Tile
              label="Not expected"
              value={summary.unexpected}
              tone={summary.unexpected ? 'warn' : undefined}
            />
          </div>

          <Card>
            <CardBody>
              <form onSubmit={submitScan} className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Scan or type a mould ID"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="flex-1"
                  aria-label="Mould ID"
                />
                <Button variant="primary" type="submit">
                  <ScanLine className="size-4" /> Count
                </Button>
              </form>
              {flash ? (
                <p
                  className={cn(
                    'mt-2 text-sm',
                    /not expected/.test(flash) ? 'text-warning-foreground' : 'text-success',
                  )}
                >
                  {flash}
                </p>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  {active.reference} at {active.location?.name}. The box stays focused so a handheld
                  scanner types straight into it.
                </p>
              )}
            </CardBody>
          </Card>

          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-sunken/60 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Mould</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Expected</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Counted</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[...active.lines]
                  // Problems first: a clean line needs no attention, and the
                  // point of the screen is what does.
                  .sort((a, b) => {
                    const rank = (l: typeof a) => (l.expected && !l.found ? 0 : !l.expected ? 1 : 2);
                    return rank(a) - rank(b);
                  })
                  .map((l) => {
                    const missing = l.expected && !l.found;
                    const unexpected = !l.expected && l.found;
                    return (
                      <tr key={l.id} className={missing ? 'bg-danger-subtle/30' : undefined}>
                        <td className="px-3 py-2">
                          <Code>{l.mould?.assetCode ?? '-'}</Code>{' '}
                          <span className="text-muted-foreground">{l.mould?.name}</span>
                        </td>
                        <td className="px-3 py-2">{l.expected ? 'Yes' : '-'}</td>
                        <td className="px-3 py-2">{l.found ? 'Yes' : '-'}</td>
                        <td className="px-3 py-2">
                          {missing ? (
                            <Badge tone="danger">Missing</Badge>
                          ) : unexpected ? (
                            <Badge tone="warning">Not expected here</Badge>
                          ) : (
                            <Badge tone="success">Matches</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Page>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn' | 'bad';
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'bad'
            ? 'text-2xl font-semibold text-danger'
            : tone === 'warn'
              ? 'text-2xl font-semibold text-warning-foreground'
              : tone === 'ok'
                ? 'text-2xl font-semibold text-success'
                : 'text-2xl font-semibold'
        }
      >
        {value}
      </div>
    </div>
  );
}
